import { County } from "@app/geography";
import { RegionSet, RegionGranularity } from "@app/region-sets";
import { DownloadServiceAreaButton } from "@app/region-sets/editor/DownloadServiceAreaButton";
import LookupRegionSet from "@app/region-sets/editor/lookupRegionSet";
import { ServiceAreaEditor } from "@app/region-sets/editor/ServiceAreaEditor";
import { ServiceAreaEditorContext } from "@app/region-sets/editor/ServiceAreaEditorContext";
import { FlyoutLink } from "@app/shared/Flyouts";
import { Dispatch, SetStateAction, FC, useRef, useContext, useState } from "react";
import { useHistory } from "react-router-dom";
import { RegionSetStatusType } from "@app/projects/enumerators";
import Project from "@app/projects/project";
import ProjectService from "@app/projects/projectService";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import { BreadcrumbTemplateEnum } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import { QESButton } from "@app/shared/qes-standards/Button";
import GeographyService from "@app/geography/geographyService";
import NearbyCountiesPanelContent from "@app/region-sets/editor/NearbyCountiesPanelContent";
import { CountyStatus } from "@app/projects/create/countyStatus";

type EditRegionSetProps = {
  countyStatuses: CountyStatus[];
  getUpdatedCountyStatuses: (statuses: CountyStatus[]) => CountyStatus[];
  handleSubmitNearbyCounties: (newUpdateCounties: County[], regionStatus: RegionSetStatusType) => void;
  project: Project;
  regionSet: RegionSet | null;
  setCountyStatuses: Dispatch<SetStateAction<CountyStatus[]>>;
  setUpdateCounties: Dispatch<SetStateAction<County[]>>;
  updateCounties: County[];
};

export const EditRegionSetInner: FC<EditRegionSetProps> = (props: EditRegionSetProps) => {
  const projectService = useRef(new ProjectService()).current;
  const geographyService = useRef(new GeographyService()).current;
  const serviceAreaContext = useContext(ServiceAreaEditorContext);
  const history = useHistory();
  const [isNextDisabled, setIsNextDisabled] = useState(false);
  const [submitClicked, setSubmitClicked] = useState(false);

  const handleSubmit = () => {
    setSubmitClicked(true);
    serviceAreaContext.handleComplete((handleComplete));
  }

  const handleRegionUpdate = (lookupRegionSet: LookupRegionSet) => {
    // For some reason an empty lookupRegionSet.children is not the same as {}, but serializing it works
    if (JSON.stringify(lookupRegionSet.children) !== "{}") {
      setIsNextDisabled(false);
    } else {
      setIsNextDisabled(true);
    }
  }

  const handleComplete = async (serviceAreaRegionSet: RegionSet) => {
    await projectService.updateServiceArea(props.project.projectId, serviceAreaRegionSet.regionSetId);
    await getCountyRegionSetIds();
    history.goBack();
  }

  const handleCancel = () => {
    history.goBack();
  }

  const isProjectRunning = (): boolean => !!props.project?.isRunning;

  const getCountyRegionSetIds = async () => {
    const counties = props.countyStatuses;

    const countiesWithoutRegionSet: RegionSet[] = [];
    for (const c of counties) {
      if (!c.regionSetId) {
        const newRegion: RegionSet = {
          granularity: RegionGranularity.County,
          partialRegionSets: c.countyZips.map<RegionSet>((cz) => {
            return {
              granularity: RegionGranularity.CountyZip,
              partialRegionSets: [],
              regionCode: c.countySSA,
              regionSetId: null,
              subRegionCode: cz,
            };
          }),
          regionCode: c.countySSA,
          regionSetId: null,
          subRegionCode: null,
        };

        countiesWithoutRegionSet.push(newRegion);
      }
    }

    //create all the region sets at once
    const createdRegionSets = await geographyService.getOrCreateRegionSets(countiesWithoutRegionSet);

    //match by countySSA on returned region sets to get the region set id
    for (const c of createdRegionSets) {
      const matchingCounty = counties.find((county) => county.countySSA === c.regionCode);
      matchingCounty.regionSetId = c.regionSetId;
    }

    props.setCountyStatuses(counties);
    projectService.setStatuses(props.project.projectId, counties);
  }

  const handleCountyStatusUpdates = async (statuses: CountyStatus[]) => {
    props.setCountyStatuses(props.getUpdatedCountyStatuses(statuses))
  }

  const projectName = props.project?.resource.name || "Loading...";

  if (!props.regionSet) {
    return null;
  }

  return (
    <Page documentTitle="Edit Service Area">
      <Breadcrumbs
        template={BreadcrumbTemplateEnum.NetworkAssessment}
        items={[
          {
            link: !!props.project ? `/projects/${props.project.projectId}` : "",
            text: projectName,
          },
          "Edit Service Area"
        ]}
      />
      <PageInner>
        <PageHeader title={projectName} />
        {!!props.project &&
          (
            <PageSection>
              <ServiceAreaEditor
                countyStatuses={props.countyStatuses}
                handleCountyStatusUpdates={handleCountyStatusUpdates}
                regionSet={props.regionSet}
                onUpdate={handleRegionUpdate}
                onComplete={handleComplete}
                updateCounties={props.updateCounties}
              />
              <div className="pt-1 d-flex flex-row w-100 align-items-center">
                <div className="col-3 ps-0">
                  <QESButton qesTestId="edit-region-set_cancel" qesType="secondary-danger" disabled={submitClicked} onClick={handleCancel}>Cancel</QESButton>
                </div>
                <div className="ms-3">
                  <DownloadServiceAreaButton />
                </div>
                <div className="ms-auto">
                  {((!!props.countyStatuses && props.countyStatuses.length > 0) || null) &&
                    <FlyoutLink
                      disabled={submitClicked}
                      classNames="qes-button-secondary mr15 pe-2 ps-2"
                      content={{
                        body: (<NearbyCountiesPanelContent
                          countySSAs={props.countyStatuses.map((countyStatus) => countyStatus.countySSA)}
                          onSubmitNearbyCounties={props.handleSubmitNearbyCounties}
                        />),
                        title: "Add Nearby Counties",
                      }}
                      testId="add-nearby-counties-flyout-link"
                      size="20"
                      onClose={() => { props.setUpdateCounties([]) }}
                    >
                      Add Nearby Counties
                    </FlyoutLink>
                  }
                  <QESButton qesTestId="edit-region-set_submit" qesType="primary" disabled={isProjectRunning() || isNextDisabled || submitClicked} onClick={handleSubmit}>Submit</QESButton>
                </div>
              </div>
            </PageSection>
          )
        }
      </PageInner>
    </Page >
  );
}
